<template>
  <div v-if="modelValue" class="cn-header-mobile-menu">
    <div class="cn-header-mobile-menu__content">
      <div class="cn-header-mobile-menu__accordion">
        <div
          v-for="(item, i) in menu"
          :key="item.id"
          class="cn-header-mobile-menu__item"
          :class="{ 'cn-header-mobile-menu__item--active': i === activeItem }"
        >
          <div :to="item.url" class="cn-header-mobile-menu__item-header">
            <UiLink :to="item.url">{{ item.name }}</UiLink>
            <button
              v-if="item.children && item.children.length"
              class="cn-header-mobile-menu__item-toggler"
              @click="setActiveItem(i)"
            >
              <UiIcon name="chevron-down" />
            </button>
          </div>
          <div
            v-if="item.children && item.children.length"
            class="cn-header-mobile-menu__item-content"
          >
            <ul v-if="i === activeItem">
              <li v-for="child in item.children" :key="child.id">
                <UiLink :to="child.url">
                  {{ child.name }}
                </UiLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div>
        <div class="cn-header-mobile-menu__locale-controls">
          <LocationSwitcher dark />
          <LanguageSwitcher dark />
        </div>
        <div class="cn-header-mobile-menu__socials">
          <UiLink
            v-for="social in socials"
            :key="social.name"
            :to="social.src"
            target="_blank"
            class="cn-header-mobile-menu__socials-item"
            :aria-label="social.name"
          >
            <UiIcon :name="social.name" />
          </UiLink>
        </div>
        <div class="cn-header-mobile-menu__controls">
          <UiButton
            variant="yellow"
            @click="supportModal !== null ? supportModal.open() : null"
          >
            {{ $t('write_to_us') }}
          </UiButton>
          <UiModal ref="supportModal" :title="$t('submit_an_inquiry')">
            <SupportForm />
          </UiModal>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useAccordion, useSocials } from '@/composables';
import UiModal from '@/components/ui/UiModal.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import UiLink from '@/components/ui/UiLink';
import UiIcon from '@/components/ui/UiIcon';
import UiButton from '@/components/ui/UiButton';

interface Props {
  modelValue?: boolean;
  menu: any[];
}

defineProps<Props>();

const { activeItem, setActiveItem } = useAccordion();
const { socials } = useSocials();
const supportModal = ref<null | typeof UiModal>(null);
</script>

<style lang="scss">
.cn-header-mobile-menu {
  --ab-canyon--header-mobile-menu-item-header-color-text: var(
    --ab-canyon-color-light
  );
  --ab-canyon--header-mobile-menu-item-toggler-color-text: var(
    --ab-canyon-color-light
  );
  --ab-canyon--header-mobile-menu-item-content-links-color-text: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon--header-mobile-menu-socials-item-color-bg: var(
    --ab-canyon-color-light
  );
  --ab-canyon--header-mobile-menu-socials-item-color-text: var(
    --ab-canyon-color-light
  );
  &__content {
    padding-top: var(--size-xl);
    padding-bottom: var(--size-xl);
    column-gap: var(--size-3xl);
    justify-content: space-between;
    flex-direction: column;
    display: flex;
    @media (min-width: 656px) {
      flex-direction: row;
    }
  }
  &__accordion {
    flex-grow: 1;

    @media (min-width: 656px) {
      max-width: 28rem;
    }

    > :not([hidden]) {
      ~ :not([hidden]) {
        margin-top: var(--size-base);
      }
    }
  }
  &__item {
    width: 100%;
    &--active {
      .cn-header-mobile-menu {
        &__item-toggler {
          .cn-icon {
            transform: rotate(180deg);
          }
        }
      }
    }
  }
  &__item-header {
    color: var(--ab-canyon--header-mobile-menu-item-header-color-text);
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.75rem;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    display: flex;
  }
  &__item-toggler {
    color: var(--ab-canyon--header-mobile-menu-item-toggler-color-text);
    .cn-icon {
      width: var(--size-lg);
      height: var(--size-lg);
    }
  }
  &__item-content {
    ul {
      margin-top: var(--size-base);
      margin-left: var(--size-xs);
      > :not([hidden]) {
        ~ :not([hidden]) {
          margin-top: var(--size-base);
        }
      }
    }
    a {
      color: var(--ab-canyon--header-mobile-menu-item-content-links-color-text);
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &__locale-controls {
    margin-top: var(--size-xl);
  }
  &__socials {
    gap: var(--size-xl);
    display: flex;
    margin-top: var(--size-3xl);
  }
  &__socials-item {
    background-color: color-mix(
      in srgb,
      var(--ab-canyon--header-mobile-menu-socials-item-color-bg),
      transparent 90%
    );
    color: var(--ab-canyon--header-mobile-menu-socials-item-color-text);
    justify-content: center;
    align-items: center;
    width: var(--size-5xl);
    height: var(--size-5xl);
    display: flex;

    .cn-icon {
      width: 1.75rem;
      height: 1.75rem;
    }
  }
  &__controls {
    margin-top: var(--size-3xl);
  }
}
</style>
